import { motion } from "framer-motion";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  Users,
  Target,
  Shield,
  TrendingUp,
  BookOpen,
  Eye,
} from "lucide-react";

const philosophy = [
  { icon: Shield, text: "Capital protection first" },
  { icon: TrendingUp, text: "Risk-managed growth" },
  { icon: Target, text: "Long-term thinking" },
  { icon: Eye, text: "Transparency" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              About <span className="text-primary">PassivePay</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground">
              Building financial freedom through disciplined investing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold">
                Our <span className="text-primary">Story</span>
              </h2>
            </div>

            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                PassivePay was created to help individuals achieve financial freedom through 
                disciplined investing, not speculation. With over a decade of experience in 
                the markets, we've seen what works and what doesn't.
              </p>
              <p>
                We've guided countless investors through bull markets and bear markets alike, 
                always focusing on sustainable growth rather than quick gains. Our approach 
                is simple: education first, execution second.
              </p>
              <p>
                What started as helping friends and family has grown into a community of 
                like-minded individuals all working towards the same goal - financial 
                independence and early retirement.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-primary/10">
                <div className="text-3xl font-heading font-bold text-primary">10+</div>
                <div className="text-sm text-muted-foreground">Years Experience</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-secondary/10">
                <div className="text-3xl font-heading font-bold text-secondary">100+</div>
                <div className="text-sm text-muted-foreground">Investors Guided</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-accent/10">
                <div className="text-3xl font-heading font-bold text-accent">24/7</div>
                <div className="text-sm text-muted-foreground">Community Support</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Our <span className="text-primary">Philosophy</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {philosophy.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-xl p-6 text-center hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="font-medium">{item.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12 border-primary/30 neon-glow text-center"
          >
            <Target className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-6">
              Our <span className="text-primary">Vision</span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              To make passive income accessible globally through structure, 
              education and aligned incentives.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
